// src/pages/dataInput/Step1Source.tsx
import React from "react";
import { cls } from "../theme/theme";
import {
  SourcePayload, SourceKind, Tool,
} from "./types";
import FileSource    from "./sources/FileSource";
import DDLSource     from "./sources/DDLSource";
import DBSource      from "./sources/DBSource";
import CatalogSource from "./sources/CatalogSource";

interface Props {
  tool:       Tool;
  payload:    SourcePayload | null;
  onChange:   (p: SourcePayload | null) => void;
}

const TABS: {
  kind: SourceKind;
  icon: string;
  label: string;
  sub: string;
  dgOnly?: boolean;
}[] = [
  { kind: "file",    icon: "📂", label: "Files",            sub: "CSV, Excel" },
  { kind: "ddl",     icon: "📜", label: "DDL",              sub: ".sql / CREATE TABLE", dgOnly: true },
  { kind: "db",      icon: "🔌", label: "Database",         sub: "Postgres, Snowflake, …" },
  { kind: "catalog", icon: "📚", label: "DG Catalog",       sub: "Collibra, Alation",  dgOnly: true },
];

export const Step1Source: React.FC<Props> = ({ tool, payload, onChange }) => {
  const isDG = tool === "BUILD_DG_USING_DATA";
  const visibleTabs = TABS.filter(t => isDG || !t.dgOnly);

  // Default sheet mode: DG → one entity per sheet, DQ/MDM → group as one entity
  const defaultSheetMode = isDG ? "separate_entities" : "sub_entities";

  const [active, setActive] = React.useState<SourceKind>(payload?.kind ?? "file");

  const switchTab = (k: SourceKind) => {
    setActive(k);
    if (payload && payload.kind !== k) {
      onChange(null);
    }
  };

  return (
    <div>
      <div className="gw-section-hdr">Pick where the data comes from</div>

      <div className="gw-src-tabs">
        {visibleTabs.map(t => (
          <button
            key={t.kind}
            className={cls("gw-src-tab", active === t.kind && "active")}
            onClick={() => switchTab(t.kind)}
            type="button"
          >
            <span className="gw-src-tab-icon">{t.icon}</span>
            <span className="gw-src-tab-text">
              <span className="gw-src-tab-label">{t.label}</span>
              <span className="gw-src-tab-sub">{t.sub}</span>
            </span>
          </button>
        ))}
      </div>

      <div>
        {active === "file" && (
          <FileSource
            tool={tool}
            initialFiles={payload?.kind === "file" ? payload.files : []}
            initialSheetConfigs={payload?.kind === "file" ? payload.sheetConfigs : []}
            initialSheetMode={payload?.sheetMode ?? defaultSheetMode}
            onChange={onChange}
          />
        )}

        {active === "ddl" && isDG && (
          <DDLSource
            initialSheetMode={payload?.sheetMode ?? defaultSheetMode}
            onChange={onChange}
          />
        )}

        {active === "db" && (
          <DBSource
            initialSource={payload?.kind === "db" ? payload.dbSource ?? null : null}
            initialSheetMode={payload?.sheetMode ?? defaultSheetMode}
            onChange={onChange}
          />
        )}

        {active === "catalog" && isDG && (
          <CatalogSource
            initialSheetMode={payload?.sheetMode ?? defaultSheetMode}
            onChange={onChange}
          />
        )}
      </div>
    </div>
  );
};

export default Step1Source;